import type { Command } from 'prosemirror-state';

import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type {
	GetRemixFloatingToolbarButton,
	MalleableUiPlugin,
	RemixDisabledReason,
} from '../../../malleableUiPluginType';
import { messages } from '../../messages';
import { getRemixDisabledTooltip } from '../../remix-disabled-tooltip';

const remixFloatingToolbarClickAnalyticsPayload = {
	action: ACTION.CLICKED,
	actionSubject: ACTION_SUBJECT.BUTTON,
	actionSubjectId: 'remixModalFromToolbar',
	eventType: EVENT_TYPE.UI,
	source: 'remix',
} as const;

/**
 * Factory that returns the `getRemixFloatingToolbarButton` action.
 *
 * Builds a floating-toolbar item that renders a button that opens the Remix modal.
 */
export function makeRemixFloatingToolbarButton({
	api,
	options,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	options?: {
		isDisabled?: boolean;
		isRemixHidden?: boolean;
		remixDisabledReason?: RemixDisabledReason;
	};
}): GetRemixFloatingToolbarButton {
	return ({ node, intl }) => {
		if (!api || !node) {
			return undefined;
		}

		if (options?.isRemixHidden) {
			return undefined;
		}

		if (!fg('platform_editor_ai_remix_toolbar') || !fg('maui_remixable_page_native_embeds')) {
			return undefined;
		}

		const isRemixing =
			api?.malleableUi?.sharedState.currentState()?.isRemixModalOpen ||
			api?.malleableUi?.sharedState.currentState()?.isThinking;
		const areAiEntryPointsDisabled =
			api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled;
		const disabled = options?.isDisabled || isRemixing || areAiEntryPointsDisabled;

		// cc_maui_create_keyword: when enabled, swap the "Remix" keyword for "Create".
		const isCreateKeywordEnabled = expValEquals('cc_maui_create_keyword', 'isEnabled', true);
		// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
		expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);

		let tooltipContent = intl.formatMessage(
			isCreateKeywordEnabled ? messages.createToolbarTooltip : messages.remixToolbarTooltip,
		);
		if (isRemixing) {
			tooltipContent = intl.formatMessage(
				isCreateKeywordEnabled ? messages.createInProgressTooltip : messages.remixInProgressTooltip,
			);
		} else if (options?.remixDisabledReason) {
			tooltipContent = getRemixDisabledTooltip(intl.formatMessage, options?.remixDisabledReason);
		} else if (fg('cc_maui_updated_remix_tooltip_text')) {
			tooltipContent = intl.formatMessage(
				isCreateKeywordEnabled
					? messages.createBlockTooltipUpdated
					: messages.remixToolbarTooltipUpdated,
			);
		}

		const onClick: Command = (editorState) => {
			if (disabled) {
				return false;
			}
			const $pos = editorState.selection.$from;
			let nodePos: number | undefined;
			for (let depth = $pos.depth; depth > 0; depth--) {
				if ($pos.node(depth) === node) {
					nodePos = $pos.before(depth);
					break;
				}
			}
			api.analytics?.actions.fireAnalyticsEvent<
				typeof remixFloatingToolbarClickAnalyticsPayload,
				'customEventType'
			>(remixFloatingToolbarClickAnalyticsPayload);
			api.core.actions.execute(
				api.malleableUi.commands.openRemixModal({
					nodePos,
					nodeType: node.type.name,
					metadata: {
						interactionSource: 'remixModalFromFloatingToolbar',
					},
					source: 'floatingToolbar',
				}),
			);
			return true;
		};

		return {
			id: 'editor.malleable-ui.remix',
			type: 'button',
			title: intl.formatMessage(
				isCreateKeywordEnabled ? messages.createToolbarLabel : messages.remixToolbarLabel,
			),
			tooltipContent,
			showTitle: true,
			hideTooltipOnClick: false,
			icon: AiGenerativeRemixIcon,
			onClick,
			disabled,
			testId: 'editor-floating-toolbar__remix-button',
		};
	};
}
